import { Button } from '@avanta/shared/components/ui/Button';
import { Select } from '@avanta/shared/components/ui/Select';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useGetSpecialityOptions } from '@avanta/shared/hooks/useGetSpecialityOptions';
import { cn } from '@avanta/shared/utils/cn';
import {
  Building,
  ChevronLeft,
  ChevronRight,
  ClipboardPen,
  MapPin,
  Stethoscope,
} from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AppointmentsReservePayload,
  type AppointmentsReserveFormData,
  type StepProps,
} from 'pages/AppointmentsReserve/types';
import { mapSpecialities, mapTreatments } from 'pages/AppointmentsReserve/utils';
import { StepSection } from '../../ui/StepSection';
import { AppointmentsCalendar } from './components/AppointmentsCalendar';
import { SearchPsychologistBanner } from './components/SearchPsychologistBanner';
import { SessionStepSkeleton } from './components/SessionStepSkeleton';
import { useFetchTopPsychologists } from './hooks/useFetchTopPsychologists';
import { useFetchTreatments } from './hooks/useFetchTreatments';
import { useSpecialities } from './hooks/useSpecialities';

export const SessionStep = ({ handleNext, handleBack }: StepProps): React.ReactElement => {
  const { reset, setValue, watch } = useFormContext<AppointmentsReserveFormData>();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { specialities, workCenters, isLoading: isSpecialitiesLoading } = useSpecialities();
  const { data: treatments, isLoading: isTreatmentsLoading } = useFetchTreatments();
  const centersOptions = useGetSpecialityOptions(
    AppointmentsReservePayload.backofficeCenterId,
    workCenters
  );
  const citiesOptions = useGetSpecialityOptions(AppointmentsReservePayload.city, workCenters);
  const { psychologist: psychologistData, isLoading: isPsychologistLoading } =
    useFetchTopPsychologists();
  const isLoading = isSpecialitiesLoading || isTreatmentsLoading;
  const [
    specialityBooked,
    psychologistServiceBooked,
    city,
    backofficeCenterId,
    psychologistForm,
    flow,
  ] = watch([
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.psychologistServiceBooked,
    AppointmentsReservePayload.city,
    AppointmentsReservePayload.backofficeCenterId,
    AppointmentsReservePayload.psychologist,
    AppointmentsReservePayload.flow,
  ]);

  const isEditFlow = flow === FLOW_MODES.edit;
  const psychologist = psychologistForm ?? psychologistData;

  const handleOnSpecialityChange = (value: string): void => {
    reset(formValues => ({
      ...formValues,
      [AppointmentsReservePayload.specialityBooked]: Number(value),
      [AppointmentsReservePayload.psychologistServiceBooked]: undefined,
      [AppointmentsReservePayload.city]: '',
      [AppointmentsReservePayload.backofficeCenterId]: '',
      [AppointmentsReservePayload.dateTime]: '',
    }));
    if (flow !== FLOW_MODES.search) {
      setValue(AppointmentsReservePayload.psychologist, undefined);
    }
  };

  const handleOnCityChange = (value: string): void => {
    reset(formValues => ({
      ...formValues,
      [AppointmentsReservePayload.city]: value,
      [AppointmentsReservePayload.backofficeCenterId]: '',
      [AppointmentsReservePayload.dateTime]: '',
    }));
    if (flow !== FLOW_MODES.search) {
      setValue(AppointmentsReservePayload.psychologist, undefined);
    }
  };

  const handleOnCenterChange = (value: string): void => {
    reset(formValues => ({
      ...formValues,
      [AppointmentsReservePayload.backofficeCenterId]: value,
      [AppointmentsReservePayload.dateTime]: '',
    }));
    if (flow !== FLOW_MODES.search) {
      setValue(AppointmentsReservePayload.psychologist, undefined);
    }
  };

  const handleOnContinue = (): void => {
    if (!psychologist) return;
    setValue(AppointmentsReservePayload.psychologist, psychologist);
    handleNext?.();
  };

  if (isLoading) {
    return <SessionStepSkeleton />;
  }

  return (
    <div className="flex flex-col gap-10 w-3xl">
      <SearchPsychologistBanner />
      <StepSection
        title={t('AppointmentsReserve.Steps.session.title')}
        className="flex w-full gap-6 *:flex-1"
        containerClassName="w-full"
      >
        <Select
          label={t('AppointmentsReserve.Steps.session.speciality.label')}
          options={mapSpecialities(specialities ?? [])}
          placeholder={t('AppointmentsReserve.Steps.session.speciality.placeholder')}
          leftIcon={ClipboardPen}
          value={specialityBooked ? String(specialityBooked) : undefined}
          onValueChange={handleOnSpecialityChange}
          readOnly={isEditFlow}
        />
        <Select
          label={t('AppointmentsReserve.Steps.session.treatment.label')}
          options={mapTreatments(specialityBooked, treatments ?? [])}
          placeholder={t('AppointmentsReserve.Steps.session.treatment.placeholder')}
          disabled={!specialityBooked}
          leftIcon={Stethoscope}
          value={psychologistServiceBooked ? String(psychologistServiceBooked) : undefined}
          onValueChange={value =>
            setValue(AppointmentsReservePayload.psychologistServiceBooked, Number(value))
          }
          readOnly={isEditFlow}
        />
      </StepSection>
      <StepSection
        title={t('AppointmentsReserve.Steps.session.location.title')}
        containerClassName="w-full"
        className="flex w-full gap-6 *:flex-1"
      >
        <Select
          label={t('AppointmentsReserve.Steps.session.city.label')}
          options={citiesOptions}
          placeholder={t('AppointmentsReserve.Steps.session.city.placeholder')}
          leftIcon={MapPin}
          value={city}
          disabled={!specialityBooked && !psychologistServiceBooked}
          onValueChange={handleOnCityChange}
          readOnly={isEditFlow}
        />
        <Select
          label={t('AppointmentsReserve.Steps.session.center.label')}
          options={centersOptions}
          placeholder={t('AppointmentsReserve.Steps.session.center.placeholder')}
          leftIcon={Building}
          value={backofficeCenterId}
          disabled={!city}
          onValueChange={handleOnCenterChange}
          readOnly={isEditFlow}
        />
      </StepSection>
      {backofficeCenterId && <AppointmentsCalendar />}
      <div className={cn('flex w-full justify-between', isEditFlow && 'justify-end')}>
        {isEditFlow ? null : (
          <Button
            type="button"
            size="lg"
            icon="back"
            variant="terciary"
            className="w-fit"
            onClick={() => handleBack?.()}
          >
            <ChevronLeft className="size-6" />
            {t('Common.back', { ns: LOCIZE_NS.COMMON })}
          </Button>
        )}
        <Button
          type="button"
          size="lg"
          icon="trailing"
          className="min-w-37 w-fit"
          disabled={!psychologist}
          isLoading={isPsychologistLoading}
          onClick={handleOnContinue}
        >
          {t('AppointmentsReserve.next')}
          <ChevronRight className="size-6" />
        </Button>
      </div>
    </div>
  );
};
